import { BottomSheet, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { PersonalIdSubtypeSelector } from "@src/appV2/redesign/Documents/Details/Workflows/Stripe/PersonalIdSubtypeSelector";
import { useStripeIdentityVerificationFlow } from "@src/appV2/redesign/Documents/Details/Workflows/Stripe/useStripeIdentityVerificationFlow";
import { StripeIdentityVerificationFlowStatus } from "@src/appV2/Stripe";
import { useCallback, useRef, useState } from "react";

import { type OnboardingPersonalIdVerificationAnalytics } from "./types";

interface PersonalIdVerificationSelectSubtypeBottomSheetProps {
  requirementId: string;
  modalState: UseModalState;
  onVerificationCompleted: () => void;
  analytics: OnboardingPersonalIdVerificationAnalytics;
}

export function OnboardingPersonalIdVerificationSelectSubtypeBottomSheet(
  props: PersonalIdVerificationSelectSubtypeBottomSheetProps
) {
  const stripeVerificationInitiated = useRef(false);
  const { requirementId, modalState, onVerificationCompleted, analytics } = props;

  const [selectedPersonalIdSubtype, setSelectedPersonalIdSubtype] = useState<string | undefined>(
    undefined
  );

  const { isLoading, startStripeVerificationFlow } = useStripeIdentityVerificationFlow({
    requirementId,
    // Automatically update the worker name to the name from the personal ID document
    autoUpdateWorkerName: true,
  });

  async function handleOnContinue() {
    stripeVerificationInitiated.current = true;
    if (isDefined(selectedPersonalIdSubtype)) {
      const { stripeVerificationStatus } = await startStripeVerificationFlow({
        personalIdSubtype: selectedPersonalIdSubtype,
      });

      // Log event if user cancels the verification after starting
      const isStripeVerificationCanceled =
        stripeVerificationStatus === StripeIdentityVerificationFlowStatus.CANCELED;
      if (isStripeVerificationCanceled) {
        logEvent(APP_V2_USER_EVENTS.ONBOARDING_PERSONAL_ID_ABANDONED_VERIFICATION, {
          ...analytics,
          requirementId,
          selectedPersonalIdSubtype,
        });
      }

      const isStripeVerificationCompleted =
        stripeVerificationStatus === StripeIdentityVerificationFlowStatus.COMPLETED;
      if (isStripeVerificationCompleted) {
        onVerificationCompleted();
      }
    }
  }

  const handleClose = useCallback(() => {
    // Log event if user closes the bottom sheet without initiating the verification
    if (!stripeVerificationInitiated.current) {
      logEvent(APP_V2_USER_EVENTS.ONBOARDING_PERSONAL_ID_ABANDONED_SUBTYPE_SELECT, {
        ...analytics,
        requirementId,
        selectedPersonalIdSubtype,
      });
    }

    modalState.closeModal();
  }, [analytics, requirementId, selectedPersonalIdSubtype, modalState]);

  return (
    <BottomSheet
      canDismiss
      footer={<DialogFooter orientation="vertical" closeButtonAriaLabel="Close Select ID Type" />}
      header={<BottomSheetHeader onClose={handleClose} />}
      modalState={modalState}
      onClose={handleClose}
    >
      <DialogContent>
        <PersonalIdSubtypeSelector
          selectedSubtype={selectedPersonalIdSubtype}
          isLoading={isLoading}
          onSubtypeChange={setSelectedPersonalIdSubtype}
          onContinue={async () => {
            await handleOnContinue();
          }}
        />
      </DialogContent>
    </BottomSheet>
  );
}
